// Massive (formerly Polygon.io) Starter: 15-minute delayed snapshots, 5y aggregates, ticker search, news.
import { t } from '@/i18n';
import type { Quote } from '@/types';
import { MarketError, marketFetch } from './errors';

const BASE = 'https://api.polygon.io';

function get(path: string, params: Record<string, string>, apiKey: string, signal?: AbortSignal) {
  const url = new URL(`${BASE}${path}`);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  return marketFetch(url.toString(), { headers: { Authorization: `Bearer ${apiKey}` }, signal }, 'Massive');
}

interface SnapshotTicker {
  ticker: string;
  updated?: number; // ns
  todaysChange?: number;
  todaysChangePerc?: number;
  day?: { o?: number; h?: number; l?: number; c?: number };
  prevDay?: { c?: number };
  min?: { c?: number; t?: number };
  lastTrade?: { p?: number; t?: number };
}

export async function massiveSnapshots(symbols: string[], apiKey: string, signal?: AbortSignal): Promise<Quote[]> {
  if (symbols.length === 0) return [];
  const json = (await (await get('/v2/snapshot/locale/us/markets/stocks/tickers', { tickers: symbols.join(',') }, apiKey, signal)).json()) as { tickers?: SnapshotTicker[] };
  return (json.tickers ?? []).flatMap((t) => {
    const price = t.lastTrade?.p ?? t.min?.c ?? t.day?.c ?? t.prevDay?.c;
    if (!price) return [];
    const asOf = t.lastTrade?.t ? t.lastTrade.t / 1e6 : t.updated ? t.updated / 1e6 : Date.now();
    return [{
      key: `eq:${t.ticker.toUpperCase()}`,
      price,
      currency: 'USD',
      asOf,
      change: t.todaysChange,
      changePct: t.todaysChangePerc,
      previousClose: t.prevDay?.c,
      // Before the first trade of a day the snapshot's day bar is all zeros.
      open: t.day?.o || undefined,
      high: t.day?.h || undefined,
      low: t.day?.l || undefined,
      source: 'massive' as const,
      freshness: 'delayed15' as const,
      stale: false,
    }];
  });
}

export interface DailyBar { t: number; o: number; h: number; l: number; c: number; v: number }

export async function massiveDailyBars(symbol: string, from: string, to: string, apiKey: string, signal?: AbortSignal): Promise<DailyBar[]> {
  const json = (await (await get(`/v2/aggs/ticker/${encodeURIComponent(symbol)}/range/1/day/${from}/${to}`, { adjusted: 'true', sort: 'asc', limit: '5000' }, apiKey, signal)).json()) as { results?: DailyBar[] };
  return json.results ?? [];
}

export interface NewsItem { id: string; title: string; publisher: string; publishedAt: number; url: string }

export async function massiveNews(symbol: string, apiKey: string, limit = 10, signal?: AbortSignal): Promise<NewsItem[]> {
  const json = (await (await get('/v2/reference/news', { ticker: symbol, limit: String(limit), order: 'desc' }, apiKey, signal)).json()) as {
    results?: { id: string; title: string; publisher?: { name?: string }; published_utc: string; article_url: string }[];
  };
  return (json.results ?? []).map((n) => ({ id: n.id, title: n.title, publisher: n.publisher?.name ?? '', publishedAt: Date.parse(n.published_utc), url: n.article_url }));
}

export interface TickerHit { symbol: string; name: string; type: string; exchange?: string }

export async function massiveSearch(query: string, apiKey: string, signal?: AbortSignal): Promise<TickerHit[]> {
  const json = (await (await get('/v3/reference/tickers', { search: query, market: 'stocks', active: 'true', limit: '20' }, apiKey, signal)).json()) as {
    results?: { ticker: string; name: string; type?: string; primary_exchange?: string }[];
  };
  return (json.results ?? []).map((r) => ({ symbol: r.ticker, name: r.name, type: r.type ?? '', exchange: r.primary_exchange }));
}

export async function verifyMassive(apiKey: string): Promise<string> {
  const quotes = await massiveSnapshots(['AAPL'], apiKey);
  if (quotes.length === 0) throw new MarketError('upstream', t('err.noSnapshot', { symbol: 'AAPL' }));
  return `AAPL ${quotes[0].price.toFixed(2)}`;
}
